

import React, { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EditCourse from './EditCourse';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { CourseManagment } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import AISuggestions from '../../components/AISuggestions';
import { useCourseCategories } from '../../providers/CourseCategoryProvider';
import { SearchCoursesOutput } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/PathItems';
import { useCourses } from '../../providers/CourseProvider';



const allCategories = [
  'Puppy',
  'Obedience',
  'Agility',
  'Tricks',
];


function Courses() {
  const { courseCategories, loadCourseCategories } = useCourseCategories()
  const { loadCourse } = useCourses();
  const { onError } = useToast();
  const [search, setSearch] = useState('');
  const [coursesList, setCoursesList] = useState<SearchCoursesOutput[]>([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCourseName, setNewCourseName] = useState('');
  const [editingCourse, setEditingCourse] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<{ id: string, name: string } | null>(null);

  const searchCourses = async (query: string) => {
    onError((await Session.getClient().request(CourseManagment.searchCourses({ query, limit: 100, offset: 0 })))
      .maybeOk(setCoursesList));
  };

  useEffect(() => {
    loadCourseCategories();
    searchCourses('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDialogOpen = () => {
    setNewCourseName('');
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewCourseName('');
  };

  const handleDialogOk = async () => {
    if (newCourseName.trim()) {
      const result = await Session.getClient().request(CourseManagment.createCourse({
        name: newCourseName.trim(),
        description: '',
        type: 'person',
        breedIds: [],
        categories: [],
      }));
      await onError(result.maybeOk(() => searchCourses(search)));
    }
    handleDialogClose();
  };

  const handleDeleteClick = (id: string, name: string) => {
    setCourseToDelete({ id, name });
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setCourseToDelete(null);
  };

  const handleDeleteDialogOk = async () => {
    if (courseToDelete) {
      await onError((await Session.getCourseClient(courseToDelete.id).request(CourseManagment.deleteCourse()))
        .maybeOk(() => searchCourses(search)));
    }
    handleDeleteDialogClose();
  };

  const filteredCourses: SearchCoursesOutput[] = coursesList.filter((c: SearchCoursesOutput) => {
    const matchesSearch = c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.description?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !categoryFilter || (c.courseCategoryIds && c.courseCategoryIds.includes(categoryFilter));
    return matchesSearch && matchesCategory;
  });

  if (editingCourse) {
    return <EditCourse courseId={editingCourse} onBack={() => {
      searchCourses(search);
      setEditingCourse(null);
    }} />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="Search courses"
            size="small"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ width: 250 }}
          />
          <Select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            displayEmpty
            size="small"
            sx={{ minWidth: 180 }}
          >
            <MenuItem value=""><em>All Categories</em></MenuItem>
            {allCategories.map(cat => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <AISuggestions 
            client={Session.getClient()}
            currentSnapshot={JSON.stringify(filteredCourses)}
            usageInstructions={CourseManagment.toAgentInstructions()}
            developerMessage={'The user is in the view where they can search and see a list of courses. For any of the operations ' +
              'regarding a specific course the path parameter with the name courseId MUST be specified with that course id and if ' +
              'modifying a lesson the additional path parameter lessonId MUST also be specified with that lesson id.'}
            onOperationsApplied={() => searchCourses(search)}
          />
        </Box>
        <Button variant="contained" color="primary" onClick={handleDialogOpen}>New Course</Button>
      </Box>
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Add Course</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Course Name"
            fullWidth
            autoFocus
            value={newCourseName}
            onChange={e => setNewCourseName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDialogOk} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><Typography fontWeight="bold">Name</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Description</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Lesson count</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Categories</Typography></TableCell>
              <TableCell><Typography fontWeight="bold">Published</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight="bold">Actions</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCourses.map((row) => (
              <TableRow key={row.courseId}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{row.lessonCount}</TableCell>
                <TableCell>{row.courseCategoryIds.map((catId: string) => courseCategories.find(cat => cat.id === catId)?.name).join(', ')}</TableCell>
                <TableCell>
                  <Switch checked={row.state.state === 'published'} disabled />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="primary" onClick={() => {
                    loadCourse(row.courseId);
                    setEditingCourse(row.courseId)
                  }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteClick(row.courseId, row.name)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>Delete Course</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the course "{courseToDelete?.name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteDialogOk} variant="contained" color="error">OK</Button>
        </DialogActions>
      </Dialog>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
export default Courses;
